/**
 * The offering vocabulary, for investors.
 *
 * ---------------------------------------------------------------------------
 * COPIED FROM investor-app/src/utils/offering.js. Keep it byte-identical below
 * this header.
 *
 * Here the investor is an ORGANISATION spending its treasury, not an individual.
 * That changes who is bidding, not what the words mean: the backend records the
 * difference as investor_model and settles both the same way. So the vocabulary
 * must not fork — a company reading "Closed, being priced" has to be reading the
 * same rule an individual reads on investor-app.
 *
 * The one thing this app can do that investor-app cannot: it KNOWS its principal
 * is an organisation, so tranche audience eligibility (TrancheInvestors) can be
 * checked before submitting rather than surfacing as an API rejection.
 * ---------------------------------------------------------------------------
 *
 * A deliberate fourth copy. The sponsor portal has its own (GE-deal-portal
 * src/utils/offering.js) and the backend publishes the source of truth in
 * `@getequity/service-utils` — but no frontend imports that package, so each restates it.
 *
 * The sponsor and investor copies are NOT interchangeable, and merging them would be a mistake:
 *
 *
 *   - The portal reads TokenRequests, which may carry a populated Token; this app only ever
 *     sees Tokens, so there is no request-versus-token precedence to resolve here.
 *   - Deals without an offer period fall back to different vocabularies. A sponsor sees the
 *     request's approval status; an investor sees whether the raise is still open, because an
 *     investor never sees an unapproved deal at all.
 *   - The labels differ. "Awaiting sign-off" is a sponsor's problem; an investor wants to know
 *     what it means for their money.
 *
 * What must stay in step is the vocabulary itself — the enum values and the offer-period gate.
 * If the backend adds a status, all three places need it.
 */

export const OfferingTypes = Object.freeze({
  FIXED_PRICE: "FIXED_PRICE",
  BOOK_BUILDING: "BOOK_BUILDING",
  AUCTION: "AUCTION",
  COMBINED: "COMBINED",
});

export const OfferingStatus = Object.freeze({
  DRAFT: "DRAFT",
  INTERNAL_REVIEW: "INTERNAL_REVIEW",
  ADMIN_REVIEW: "ADMIN_REVIEW",
  APPROVED: "APPROVED",
  SCHEDULED: "SCHEDULED",
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  PRICED: "PRICED",
  ALLOTMENT_PROPOSED: "ALLOTMENT_PROPOSED",
  ALLOTMENT_APPROVED: "ALLOTMENT_APPROVED",
  SETTLING: "SETTLING",
  LISTED: "LISTED",
  CANCELLED: "CANCELLED",
  EXITED: "EXITED",
});

export const AllotmentMethods = Object.freeze({
  PRO_RATA: "PRO_RATA",
  FCFS: "FCFS",
  PRICE_PRIORITY: "PRICE_PRIORITY",
});

export const UnderSubscriptionPolicies = Object.freeze({
  PROCEED_REDUCED: "PROCEED_REDUCED",
  EXTEND: "EXTEND",
  CANCEL: "CANCEL",
});

/**
 * What a winning bidder actually pays.
 *
 * UNIFORM: every winner settles at the clearing level, however far above it they bid.
 * MULTIPLE: every winner settles at their own bid ("pay your bid"), so bidding aggressively
 * to secure an allotment is paid for in the price.
 *
 * A strike bid named no level, so it settles at the clearing one under either convention.
 */
export const AuctionPricing = Object.freeze({
  UNIFORM: "UNIFORM",
  MULTIPLE: "MULTIPLE",
});

/**
 * Who a tranche of a combined offering will take bids from.
 *
 * The two specific values are the entity_type strings the API compares against, not display
 * labels, which is why they are lowercase where every other enum here is not.
 */
export const TrancheInvestors = Object.freeze({
  ANY: "ANY",
  USER: "user",
  ORGANISATION: "organisation",
});

/**
 * The audience restriction on a tranche, or an empty string where there is none.
 *
 * The investor app does not know whether the signed-in account is an individual or an
 * organisation, so this describes the restriction rather than filtering on it. The API refuses
 * an ineligible bid with a message naming the tranche, and that stays the authority.
 */
export const trancheAudienceLabel = (tranche) => {
  if (tranche?.investors === TrancheInvestors.ORGANISATION) return "Organisations only";
  if (tranche?.investors === TrancheInvestors.USER) return "Individual investors only";
  return "";
};

export const OFFERING_TYPE_LABELS = Object.freeze({
  [OfferingTypes.FIXED_PRICE]: "Fixed price",
  [OfferingTypes.BOOK_BUILDING]: "Book building",
  [OfferingTypes.AUCTION]: "Auction",
  [OfferingTypes.COMBINED]: "Combined",
});

/**
 * How the offer will be shared out, said plainly. These are disclosure copy, not enum names —
 * an investor is being told what happens to their money, so the sentence matters more than the
 * label. Used by the offer terms block.
 */
export const ALLOTMENT_METHOD_DISCLOSURE = Object.freeze({
  [AllotmentMethods.PRO_RATA]:
    "If demand exceeds the offer, everyone is scaled back by the same proportion.",
  [AllotmentMethods.FCFS]:
    "If demand exceeds the offer, bids are filled in the order they were received until it runs out.",
  [AllotmentMethods.PRICE_PRIORITY]:
    "Bids are filled from the best price down until the offer is exhausted.",
});

export const UNDERSUBSCRIPTION_DISCLOSURE = Object.freeze({
  [UnderSubscriptionPolicies.PROCEED_REDUCED]:
    "If the offer is not fully taken up it still goes ahead, at a reduced size.",
  [UnderSubscriptionPolicies.EXTEND]:
    "If the offer is not fully taken up the offer period may be extended.",
  [UnderSubscriptionPolicies.CANCEL]:
    "If the offer is not fully taken up it is cancelled and committed funds are released.",
});

/**
 * What a winning bid settles at, in the investor's words.
 *
 * Only an auction has a convention to state: a book build clears every bid at one level by
 * construction. The backend defaults the field to UNIFORM, so an auction with it unset does
 * settle uniformly — silence here would be a missing disclosure, not an absent rule.
 *
 * Kept as a function rather than a map because the noun turns on the deal: a bond auction is
 * bid in rates, where "bidding higher" is the losing direction.
 *
 * @param {object} token
 * @param {boolean} [isRateBased] true for Debt / Fixed Interest / Fund
 * @returns {string} empty when the offering is not an auction
 */
export const auctionPricingDisclosure = (token, isRateBased = false) => {
  if (!token || token.offering_type !== OfferingTypes.AUCTION) return "";

  const noun = isRateBased ? "rate" : "price";
  if ((token.auction_pricing || AuctionPricing.UNIFORM) === AuctionPricing.MULTIPLE) {
    return isRateBased
      ? "Each successful bid settles at its own rate, so bidding below the clearing rate earns you less than bidders who held out for more."
      : "Each successful bid settles at its own price, so bidding above the clearing price costs you more than bidders who paid it.";
  }
  return `Every successful bid settles at the same final ${noun}, however ${
    isRateBased ? "low" : "high"
  } you bid.`;
};

/**
 * Whether the economics shown on this token are settled rather than guidance.
 *
 * A fixed-price offering declares its price up front, so anything projected off it is a number
 * the deal can keep. Book builds, auctions and combined offerings DISCOVER it: until the book
 * prices, `price.buy` and `interest` are guidance, and a return projected off guidance reads as
 * a quote for terms nobody has agreed to.
 *
 * Deals predating offering types carry no `offering_type` and are fixed price by definition, so
 * they must read as settled rather than have their existing surfaces disappear.
 *
 * @param {object} token
 * @returns {boolean}
 */
export const hasSettledPricing = (token) => {
  if (!token) return false;

  const type = token.offering_type || OfferingTypes.FIXED_PRICE;
  if (type === OfferingTypes.FIXED_PRICE) return true;

  // Priced, and the discovered terms are on the token — a projection is honest again.
  //
  // `> 0` rather than a null check, matching the backend's own test for "has not been priced"
  // (TRANCHE_NOT_PRICED_ERROR in allotment-run.service.js). A zero is an unset field, not a
  // deal that priced at nothing, and it would otherwise read as settled.
  return Number(token.final_price) > 0 || Number(token.final_rate) > 0;
};

/**
 * Whether this token opted into an offer period.
 *
 * The gate for everything — not the offering type, and not the status. Matches
 * `HAS_OFFER_PERIOD` in Utility/cron/offer-scheduler.js and `hasOfferPeriod()` in
 * Exchange/utils/offering.js.
 *
 * @param {object} token
 * @returns {boolean}
 */
export const hasOfferPeriod = (token) => Boolean(token && token.offer_closes_at);

/**
 * Whether this token is taking bids right now.
 *
 * `offering_status === OPEN` alone is not enough. Tokens created since offering statuses were
 * introduced carry OPEN while still filling continuously, so branching on the status alone
 * would put a bid form in front of a deal that settles instantly.
 *
 * @param {object} token
 * @returns {boolean}
 */
export const isTakingBids = (token) =>
  hasOfferPeriod(token) && token.offering_status === OfferingStatus.OPEN;

/**
 * The statuses at which the book has stopped moving.
 *
 * CANCELLED is in deliberately. The offering was called off with bids standing, and the people
 * owed an explanation are exactly the ones who bid — hiding the demand there reads as hiding
 * the reason. The backend agrees: it keeps CANCELLED commitments in the book precisely so a
 * pulled deal still reconstructs.
 */
const BOOK_RESOLVED_STATUSES = Object.freeze([
  OfferingStatus.CLOSED,
  OfferingStatus.PRICED,
  OfferingStatus.ALLOTMENT_PROPOSED,
  OfferingStatus.ALLOTMENT_APPROVED,
  OfferingStatus.SETTLING,
  OfferingStatus.LISTED,
  OfferingStatus.CANCELLED,
  OfferingStatus.EXITED,
]);

/**
 * Whether the book may be shown to an INVESTOR.
 *
 * Not "is there a book" — there is one from the first bid, and the sponsor sees it live. This
 * is the narrower question of when showing it to the people still bidding is safe, and the
 * answer is: once they have stopped.
 *
 * A live cumulative ladder destroys the price discovery the book exists to perform. An investor
 * who can see demand stacking at a level has no reason to reveal their own valuation — they bid
 * just above the visible clearing point, and a book build collapses into a fixed-price sale at
 * whatever the earliest bidders set. It cuts the other way too: a book that looks thin at
 * T-minus-an-hour invites the withdrawals that make it thinner. This is why an IPO book is
 * confidential to the bookrunner and why auction issues are sealed-bid.
 *
 * Once closed, every one of those objections lapses. The ladder is frozen, nobody can act on it,
 * and it is the demand curve the price was set against — which is the one thing that explains an
 * allotment to the investor who was scaled back.
 *
 * Deliberately not derived from the payload's `summary.is_live`: this decides whether to ASK,
 * and asking a live book is what must not happen. The consumer checks the flag as well, because
 * the server is the authority on whether bidding has really stopped.
 *
 * @param {object} token
 * @returns {boolean}
 */
export const hasResolvedBook = (token) =>
  hasOfferPeriod(token) && BOOK_RESOLVED_STATUSES.includes(token.offering_status);

export const SubscriptionStatuses = Object.freeze({
  UNDERSUBSCRIBED: "UNDERSUBSCRIBED",
  FULLY_SUBSCRIBED: "FULLY_SUBSCRIBED",
  OVERSUBSCRIBED: "OVERSUBSCRIBED",
});

/**
 * Matches the wording the sponsor sees in admin-app's BookSummary, so a conversation between an
 * investor and a deal team is about the same book in the same words.
 */
export const SUBSCRIPTION_STATUS_LABELS = Object.freeze({
  [SubscriptionStatuses.UNDERSUBSCRIBED]: "Under-subscribed",
  [SubscriptionStatuses.FULLY_SUBSCRIBED]: "Fully subscribed",
  [SubscriptionStatuses.OVERSUBSCRIBED]: "Oversubscribed",
});

/**
 * Whether buying fills immediately, as it always has.
 *
 * @param {object} token
 * @returns {boolean}
 */
export const isContinuousFill = (token) => !hasOfferPeriod(token);

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * A coarse human interval. "Closes in 6 hours" is more useful than a ticking clock, and does
 * not need a timer to stay honest.
 *
 * @param {Date|string} target
 * @param {Date} [now]
 * @returns {string|null}
 */
export const timeUntil = (target, now = new Date()) => {
  const date = toDate(target);
  if (!date) return null;

  const ms = date.getTime() - now.getTime();
  if (ms <= 0) return null;

  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;

  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;

  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
};

/**
 * Whether a funding bar means anything for this token yet.
 *
 * Minting is deferred on a non-fixed-price offering: the token is created with `supply: 0` and
 * units are only minted once the sponsor prices the book. So before pricing there is no
 * denominator — the offer size in units does not exist. A bar in that state is not merely
 * empty, it is measuring against nothing.
 *
 * Nor is "0% funded" an honest stand-in. A book taking bids may be several times covered; it
 * has no allotment yet, which is a different thing from having no interest.
 *
 * @param {object} token
 * @returns {boolean}
 */
export const hasMeaningfulRaiseProgress = (token) =>
  Boolean(token) && Number(token.supply) > 0;

/**
 * Percentage of the offer taken up, guarded.
 *
 * `total_raised / supply` is the units basis used across the deal page. Dividing by a zero
 * supply yields Infinity or NaN, which reaches the DOM as `width: NaN%` and a percentage
 * reading "Infinity" — so every caller needs the guard, and there is one of these rather than
 * four.
 *
 * @param {object} token
 * @returns {number} 0–100
 */
export const raiseProgressPercent = (token) => {
  if (!token) return 0;
  if (token.completed_raise) return 100;
  if (!hasMeaningfulRaiseProgress(token)) return 0;

  const raised = Number(token.total_raised) || 0;
  const supply = Number(token.supply);
  return Math.min(Math.max((raised / supply) * 100, 0), 100);
};

export const CommitmentStatus = Object.freeze({
  SUBMITTED: "SUBMITTED",
  WITHDRAWN: "WITHDRAWN",
  // The offering was called off, rather than the investor pulling out. Distinct from WITHDRAWN
  // because the bid was standing at the moment it happened — which is why the book still counts
  // it, and why the wording here must not imply the investor changed their mind.
  CANCELLED: "CANCELLED",
  RELEASING: "RELEASING",
  ALLOTTED: "ALLOTTED",
  PARTIALLY_ALLOTTED: "PARTIALLY_ALLOTTED",
  REJECTED: "REJECTED",
});

/**
 * What a commitment means to the investor holding it.
 *
 * This needs both objects, and that is the whole point. A commitment stays SUBMITTED through
 * the book closing and through pricing — those things happen to the *offering*, not to the
 * bid — so reading the commitment alone would tell an investor "submitted" for weeks while
 * the world moved around them. The offering supplies the progress; the commitment supplies
 * the outcome.
 *
 * RELEASING never surfaces. It is a compare-and-set guard held for the moment between
 * claiming a commitment and the wallet write landing, and it means nothing to a person.
 *
 * @param {object} commitment - with `token` populated
 * @returns {{ label: string, tone: string, detail: string|null, canAmend: boolean, held: boolean }}
 */
export const resolveCommitmentState = (commitment) => {
  const token = commitment?.token || {};
  const offering = token.offering_status;

  switch (commitment?.status) {
    case CommitmentStatus.WITHDRAWN:
      return { label: "Withdrawn", tone: "neutral", detail: "Your funds were released.", canAmend: false, held: false };

    // Previously this fell to the default branch and reached the right answer only because a
    // commitment is cancelled when its offering is, so the offering status said so. Stated
    // explicitly: the outcome should not depend on two fields agreeing.
    case CommitmentStatus.CANCELLED:
      return {
        label: "Offer cancelled",
        tone: "neutral",
        detail: "The offer was called off, so your bid did not stand. Your funds have been released.",
        canAmend: false,
        held: false,
      };

    case CommitmentStatus.ALLOTTED:
      return { label: "Allotted in full", tone: "success", detail: null, canAmend: false, held: false };

    case CommitmentStatus.PARTIALLY_ALLOTTED:
      return {
        label: "Partially allotted",
        tone: "success",
        detail: "The offer was oversubscribed, so your bid was scaled back. The balance is back in your wallet.",
        canAmend: false,
        held: false,
      };

    case CommitmentStatus.REJECTED:
      return {
        label: "Not allotted",
        tone: "neutral",
        detail: "Your bid did not qualify at the final price. Your funds have been released.",
        canAmend: false,
        held: false,
      };

    // SUBMITTED and RELEASING both mean "still in play" to an investor. What is actually
    // happening is a property of the offering.
    default:
      switch (offering) {
        case OfferingStatus.OPEN:
          return {
            label: "Live",
            tone: "active",
            detail: "You can change or withdraw this bid until the offer closes.",
            canAmend: commitment?.status === CommitmentStatus.SUBMITTED,
            held: true,
          };
        case OfferingStatus.CLOSED:
          return { label: "Book closed", tone: "info", detail: "Waiting for the sponsor to set a final price.", canAmend: false, held: true };
        case OfferingStatus.PRICED:
          return { label: "Priced", tone: "info", detail: "A price has been set. Allotment is being worked out.", canAmend: false, held: true };
        case OfferingStatus.ALLOTMENT_PROPOSED:
          return { label: "Allotment under review", tone: "info", detail: "The proposed allotment is being reviewed for approval.", canAmend: false, held: true };
        case OfferingStatus.ALLOTMENT_APPROVED:
        case OfferingStatus.SETTLING:
          return { label: "Settling", tone: "info", detail: "Your allotment is being credited.", canAmend: false, held: true };
        case OfferingStatus.CANCELLED:
          return { label: "Offer cancelled", tone: "neutral", detail: "Your funds have been released.", canAmend: false, held: false };
        default:
          return { label: "Submitted", tone: "info", detail: null, canAmend: false, held: true };
      }
  }
};

export const MARKET_TIMEZONE = "Africa/Lagos";

/**
 * When an offer opens or closes, as a moment someone can plan around.
 *
 * Rendered in the offering's market timezone rather than the viewer's. The backend stores UTC
 * instants alongside a `market_timezone` precisely so a close means one wall-clock time for
 * everyone — an investor in London reading "5:00pm" in their own zone would be an hour wrong
 * about a deadline, which is the one thing on this page that cannot be got wrong.
 *
 * @param {Date|string} value
 * @param {string} [timezone]
 * @returns {string}
 */
export const formatOfferMoment = (value, timezone = MARKET_TIMEZONE) => {
  const date = toDate(value);
  if (!date) return "";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
      timeZoneName: "short",
    }).format(date);
  } catch {
    // An unrecognised zone should not take the card down with it.
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  }
};

/**
 * Investor-facing wording. Where the sponsor sees their own workflow, an investor is told what
 * it means for them — so "ALLOTMENT_PROPOSED" is not "allotment proposed", it is that the
 * result is coming and their money is still held.
 *
 * The pre-approval states are here for completeness only. An investor cannot reach a deal that
 * has not been approved, so if one of these ever renders, something upstream is wrong.
 */
const STATUS_LABELS = {
  [OfferingStatus.DRAFT]: { label: "Not yet open", tone: "neutral" },
  [OfferingStatus.INTERNAL_REVIEW]: { label: "Not yet open", tone: "neutral" },
  [OfferingStatus.ADMIN_REVIEW]: { label: "Not yet open", tone: "neutral" },
  [OfferingStatus.APPROVED]: { label: "Opening soon", tone: "info" },
  [OfferingStatus.SCHEDULED]: { label: "Opening soon", tone: "info" },
  [OfferingStatus.OPEN]: { label: "Open for bids", tone: "active" },
  [OfferingStatus.CLOSED]: { label: "Closed, being priced", tone: "info" },
  [OfferingStatus.PRICED]: { label: "Priced, awaiting allotment", tone: "info" },
  [OfferingStatus.ALLOTMENT_PROPOSED]: { label: "Allotment under review", tone: "info" },
  [OfferingStatus.ALLOTMENT_APPROVED]: { label: "Allotment approved", tone: "success" },
  [OfferingStatus.SETTLING]: { label: "Settling", tone: "info" },
  [OfferingStatus.LISTED]: { label: "Listed", tone: "success" },
  [OfferingStatus.CANCELLED]: { label: "Cancelled", tone: "neutral" },
  [OfferingStatus.EXITED]: { label: "Exited", tone: "neutral" },
};

/**
 * The single place that decides what state a token is in, and what to call it.
 *
 * A token with an offer period reports its offering status; one without reports whether it is
 * still raising, which is the only distinction that existed before offering types and remains
 * correct for every deal that predates them.
 *
 * @param {object} token
 * @param {Date} [now]
 * @returns {{
 *   key: string,
 *   label: string,
 *   tone: string,
 *   detail: string|null,
 *   windowed: boolean,
 *   takingBids: boolean,
 *   acceptsMoney: boolean
 * }}
 */
export const resolveTokenState = (token, now = new Date()) => {
  if (!hasOfferPeriod(token)) {
    const done = Boolean(token && token.completed_raise);
    return {
      key: done ? "COMPLETED" : "RAISING",
      label: done ? "Raise complete" : "Raising",
      tone: done ? "neutral" : "active",
      detail: null,
      windowed: false,
      takingBids: false,
      // Continuous fill: buying settles on the spot, exactly as it always has.
      acceptsMoney: !done,
    };
  }

  const status = token.offering_status;
  const known = STATUS_LABELS[status] || { label: "Not yet open", tone: "neutral" };

  let detail = null;
  if (status === OfferingStatus.SCHEDULED || status === OfferingStatus.APPROVED) {
    const until = timeUntil(token.offer_opens_at, now);
    detail = until ? `Opens in ${until}` : null;
  } else if (status === OfferingStatus.OPEN) {
    const until = timeUntil(token.offer_closes_at, now);
    detail = until ? `Closes in ${until}` : "Closing";
  }

  return {
    key: status,
    label: known.label,
    tone: known.tone,
    detail,
    windowed: true,
    takingBids: status === OfferingStatus.OPEN,
    acceptsMoney: status === OfferingStatus.OPEN,
  };
};
